"use client";

import * as React from "react";
/* Subpath imports: date-fns v4 declares its types through an `exports` map,
   and this project's tsconfig is on `moduleResolution: "node"`, which does
   not read one. Importing from the root barrel type-errors even though the
   runtime export is there. */
import { addMonths } from "date-fns/addMonths";
import { eachDayOfInterval } from "date-fns/eachDayOfInterval";
import { endOfMonth } from "date-fns/endOfMonth";
import { endOfWeek } from "date-fns/endOfWeek";
import { format } from "date-fns/format";
import { isAfter } from "date-fns/isAfter";
import { isBefore } from "date-fns/isBefore";
import { isSameDay } from "date-fns/isSameDay";
import { isSameMonth } from "date-fns/isSameMonth";
import { isToday } from "date-fns/isToday";
import { setMonth } from "date-fns/setMonth";
import { setYear } from "date-fns/setYear";
import { startOfDay } from "date-fns/startOfDay";
import { startOfMonth } from "date-fns/startOfMonth";
import { startOfWeek } from "date-fns/startOfWeek";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "lib/utils";

/* ══════════════════════════════════════════════════════════════════════════
   Calendar — the month grid behind every date control in the app.

   Every date in the product was an `<input type="date">`: 31 of them. That
   control is drawn by the operating system, so it ignored the theme entirely,
   looked different on macOS, Windows and Android, and gave no way to reach a
   month more than a few clicks away — there was no month picker and no year
   picker anywhere in the codebase.

   This is the replacement. It is deliberately a *grid*, not a menu: arrow keys
   move by day, PageUp/PageDown by month, Home/End to the ends of the week, and
   the month and year are dropdowns rather than something you step to twelve
   clicks at a time.
   ══════════════════════════════════════════════════════════════════════════ */

export interface CalendarProps {
    /** Currently selected day */
    value?: Date | null;
    onChange?: (date: Date) => void;
    /** Earliest selectable day (inclusive) */
    minDate?: Date | null;
    /** Latest selectable day (inclusive) */
    maxDate?: Date | null;
    /** 0 = Sunday (default, matches the en-US formatting used elsewhere) */
    weekStartsOn?: 0 | 1;
    /** Range highlighting — the other end of an in-progress range */
    rangeEnd?: Date | null;
    /** Years offered in the header dropdown, relative to today */
    yearRange?: { back: number; forward: number };
    className?: string;
    /** Labels the grid for assistive tech */
    "aria-label"?: string;
}

const DAY_BASE =
    "relative h-9 w-9 inline-flex items-center justify-center rounded-lg text-ui font-medium tabular-nums " +
    "transition-colors duration-100 ease-ds focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--ring)]";

export const Calendar: React.FC<CalendarProps> = ({
    value,
    onChange,
    minDate,
    maxDate,
    weekStartsOn = 0,
    rangeEnd,
    yearRange = { back: 10, forward: 10 },
    className,
    "aria-label": ariaLabel = "Calendar",
}) => {
    const today = React.useMemo(() => startOfDay(new Date()), []);
    const selected = value ? startOfDay(value) : null;

    /* The month on screen. Opens on the selection when there is one, so a date
       far in the past does not open the calendar on today and strand you. */
    const [viewMonth, setViewMonth] = React.useState<Date>(
        startOfMonth(selected ?? today)
    );

    // Following the value keeps the grid honest when the parent changes it.
    React.useEffect(() => {
        if (selected) setViewMonth(startOfMonth(selected));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value?.getTime()]);

    /* The day the arrow keys are on. Kept separate from the selection so you
       can move around without committing — the selection only changes on
       Enter, Space or a click. */
    const [focusedDay, setFocusedDay] = React.useState<Date>(selected ?? today);

    const isDisabled = React.useCallback(
        (d: Date) => {
            if (minDate && isBefore(startOfDay(d), startOfDay(minDate))) return true;
            if (maxDate && isAfter(startOfDay(d), startOfDay(maxDate))) return true;
            return false;
        },
        [minDate, maxDate]
    );

    const days = React.useMemo(() => {
        const gridStart = startOfWeek(startOfMonth(viewMonth), { weekStartsOn });
        const gridEnd = endOfWeek(endOfMonth(viewMonth), { weekStartsOn });
        return eachDayOfInterval({ start: gridStart, end: gridEnd });
    }, [viewMonth, weekStartsOn]);

    const weekdayLabels = React.useMemo(() => {
        const start = startOfWeek(new Date(), { weekStartsOn });
        return Array.from({ length: 7 }, (_, i) =>
            format(addDaysSafe(start, i), "EEEEEE")
        );
    }, [weekStartsOn]);

    const years = React.useMemo(() => {
        const centre = today.getFullYear();
        return Array.from(
            { length: yearRange.back + yearRange.forward + 1 },
            (_, i) => centre - yearRange.back + i
        );
    }, [today, yearRange.back, yearRange.forward]);

    const commit = (d: Date) => {
        if (isDisabled(d)) return;
        setFocusedDay(d);
        onChange?.(startOfDay(d));
    };

    const move = (next: Date) => {
        setFocusedDay(next);
        if (!isSameMonth(next, viewMonth)) setViewMonth(startOfMonth(next));
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        const k = e.key;
        let next: Date | null = null;
        if (k === "ArrowLeft") next = addDaysSafe(focusedDay, -1);
        else if (k === "ArrowRight") next = addDaysSafe(focusedDay, 1);
        else if (k === "ArrowUp") next = addDaysSafe(focusedDay, -7);
        else if (k === "ArrowDown") next = addDaysSafe(focusedDay, 7);
        else if (k === "Home") next = startOfWeek(focusedDay, { weekStartsOn });
        else if (k === "End") next = endOfWeek(focusedDay, { weekStartsOn });
        else if (k === "PageUp") next = addMonths(focusedDay, e.shiftKey ? -12 : -1);
        else if (k === "PageDown") next = addMonths(focusedDay, e.shiftKey ? 12 : 1);
        else if (k === "Enter" || k === " ") {
            e.preventDefault();
            commit(focusedDay);
            return;
        }
        if (next) {
            e.preventDefault();
            move(next);
        }
    };

    const inRange = (d: Date) => {
        if (!selected || !rangeEnd) return false;
        const [a, b] = isBefore(selected, rangeEnd) ? [selected, rangeEnd] : [rangeEnd, selected];
        return !isBefore(d, startOfDay(a)) && !isAfter(d, startOfDay(b));
    };

    const stepMonth = (delta: number) => setViewMonth((m) => addMonths(m, delta));

    return (
        <div className={cn("w-[17.5rem] select-none", className)}>
            {/* ── Header: month and year are pickers, not a stepper ── */}
            <div className="flex items-center gap-1.5 mb-3">
                <button
                    type="button"
                    onClick={() => stepMonth(-1)}
                    aria-label="Previous month"
                    className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-content-muted hover:text-content hover:bg-surface-hover transition-colors focus-ring flex-shrink-0"
                >
                    <ChevronLeft className="w-4 h-4" />
                </button>

                <div className="flex-1 flex items-center gap-1.5 min-w-0">
                    <label className="sr-only" htmlFor="cal-month">Month</label>
                    <select
                        id="cal-month"
                        value={viewMonth.getMonth()}
                        onChange={(e) => setViewMonth((m) => setMonth(m, Number(e.target.value)))}
                        className="flex-1 min-w-0 h-8 px-2 rounded-lg bg-surface-sunken border border-line text-ui font-semibold text-content focus-ring cursor-pointer"
                    >
                        {Array.from({ length: 12 }, (_, i) => (
                            <option key={i} value={i}>
                                {format(setMonth(viewMonth, i), "MMMM")}
                            </option>
                        ))}
                    </select>

                    <label className="sr-only" htmlFor="cal-year">Year</label>
                    <select
                        id="cal-year"
                        value={viewMonth.getFullYear()}
                        onChange={(e) => setViewMonth((m) => setYear(m, Number(e.target.value)))}
                        className="w-[4.5rem] h-8 px-2 rounded-lg bg-surface-sunken border border-line text-ui font-semibold text-content tabular-nums focus-ring cursor-pointer"
                    >
                        {years.map((y) => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                </div>

                <button
                    type="button"
                    onClick={() => stepMonth(1)}
                    aria-label="Next month"
                    className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-content-muted hover:text-content hover:bg-surface-hover transition-colors focus-ring flex-shrink-0"
                >
                    <ChevronRight className="w-4 h-4" />
                </button>
            </div>

            {/* ── Weekday header ── */}
            <div className="grid grid-cols-7 mb-1">
                {weekdayLabels.map((d, i) => (
                    <div
                        key={`${d}-${i}`}
                        className="h-8 inline-flex items-center justify-center text-micro font-semibold uppercase tracking-wide text-content-subtle"
                        aria-hidden="true"
                    >
                        {d}
                    </div>
                ))}
            </div>

            {/* ── Day grid ── */}
            <div
                role="grid"
                aria-label={ariaLabel}
                tabIndex={0}
                onKeyDown={onKeyDown}
                className="grid grid-cols-7 gap-y-0.5 focus:outline-none rounded-lg focus-visible:ring-2 focus-visible:ring-[color:var(--ring)]"
            >
                {days.map((d) => {
                    const outside = !isSameMonth(d, viewMonth);
                    const isSel = selected != null && isSameDay(d, selected);
                    const disabled = isDisabled(d);
                    const highlighted = inRange(d) && !isSel;
                    return (
                        <div
                            key={d.toISOString()}
                            role="gridcell"
                            aria-selected={isSel}
                            className="flex justify-center"
                        >
                            <button
                                type="button"
                                tabIndex={-1}
                                disabled={disabled}
                                aria-current={isToday(d) ? "date" : undefined}
                                aria-label={format(d, "EEEE d MMMM yyyy")}
                                onClick={() => commit(d)}
                                className={cn(
                                    DAY_BASE,
                                    disabled && "opacity-30 cursor-not-allowed",
                                    !disabled && !isSel && "hover:bg-surface-hover",
                                    outside && !isSel && "text-content-subtle",
                                    !outside && !isSel && "text-content",
                                    highlighted && "bg-brand-subtle text-brand-on-subtle rounded-none",
                                    isSel && "bg-brand text-brand-fg font-semibold shadow-xs",
                                    /* Today is outlined, never filled — filling it
                                       makes an unselected today look chosen, which
                                       is the most common calendar bug there is. */
                                    isToday(d) && !isSel &&
                                        "ring-1 ring-inset ring-brand-border font-semibold text-brand"
                                )}
                            >
                                {format(d, "d")}
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

/** date-fns addDays without pulling the whole import surface into every file. */
function addDaysSafe(d: Date, n: number): Date {
    const next = new Date(d);
    next.setDate(next.getDate() + n);
    return next;
}

export default Calendar;
